/**
 * Shared contract for the backoffice help system.
 *
 * One catalog, two surfaces: the page-level `i` renders the whole entry as a
 * sheet, and the small `i` beside a column header or KPI renders that same
 * field's short definition. They read the same keys, so they cannot disagree.
 *
 * Generalised from `pages/reports/guide/*`, which proved the shape on the
 * reports pages first.
 */

/**
 * How a value should be read.
 *
 * `money-signed` REQUIRES `pos` and `neg` copy — the parity test fails
 * without both. A signed money column whose sign is unexplained is the exact
 * defect this system exists to prevent: on this platform a red minus in
 * `memberWinloss` means the HOUSE won, and nothing on screen said so.
 */
export type FieldKind =
  | 'money-signed'
  | 'money-unsigned'
  | 'count'
  | 'percent'
  | 'basis-points'
  | 'status'
  | 'score'
  | 'date'
  | 'text';

/**
 * Whether pressing things on this screen actually does anything.
 *
 * The audit found ~17 screens that record an operator's intent and are read
 * by nothing — IP allowlists that block nobody, feature flags no service
 * reads, maintenance windows players never see. An operator who believes a
 * door is shut when it is open is the most dangerous state in the product, so
 * this is a first-class field rather than a footnote in the sheet.
 */
export type PageStatus =
  | 'live'
  | 'records-intent'
  | 'partially-wired';

export interface FieldDoc {
  /** Catalog key; copy lives under `<pageId>.fields.<key>`. */
  key: string;
  kind: FieldKind;
  /**
   * Literal formula, shown verbatim and never translated — it is arithmetic,
   * and a translated formula is one that can drift from the engine it
   * documents.
   */
  formula?: string;
}

export interface PageDoc {
  /** `<domain>.<page>` — e.g. `finance.deposits`. Also the i18n key prefix. */
  id: string;
  /** Route or tab this documents, for the parity test and for operators. */
  route: string;
  /** RBAC code gating it, verified against the gateway policy. */
  permission: string;
  status: PageStatus;
  fields: readonly FieldDoc[];
  /** Entries in `<pageId>.cautions`. Asserted per language. */
  cautionCount: number;
  /** Entries in `<pageId>.warnings` — layer C, irreversible/money-moving. */
  warningCount: number;
}

/** Language code → that language's copy for this domain's pages. */
export type DomainCopy = Record<string, Record<string, unknown>>;

export interface DomainGuide {
  domain: string;
  pages: readonly PageDoc[];
  /**
   * The domain's own seven language files, imported by its `index.ts`.
   *
   * Carrying copy on the domain rather than in a central loader is what keeps
   * `registry.ts` the single shared file: a new domain never edits an i18n
   * wiring file, and two domains written in parallel cannot collide.
   */
  copy: DomainCopy;
}

export const pageTitleKey = (id: string): string => `${id}.title`;
export const pageSubtitleKey = (id: string): string => `${id}.subtitle`;
export const pagePurposeKey = (id: string): string => `${id}.purpose`;
export const pageSourceKey = (id: string): string => `${id}.source`;
export const pageCautionsKey = (id: string): string => `${id}.cautions`;
export const pageWarningsKey = (id: string): string => `${id}.warnings`;

export const fieldLabelKey = (id: string, key: string): string => `${id}.fields.${key}.label`;
export const fieldDefKey = (id: string, key: string): string => `${id}.fields.${key}.def`;
export const fieldPosKey = (id: string, key: string): string => `${id}.fields.${key}.pos`;
export const fieldNegKey = (id: string, key: string): string => `${id}.fields.${key}.neg`;

/** Fields that must ship a `pos`/`neg` pair. */
export function signedFields(page: PageDoc): FieldDoc[] {
  return page.fields.filter((f) => f.kind === 'money-signed');
}
